import React, { useCallback, useEffect, useMemo, useState } from 'react';
import request from '../../../../helpers/request';
import endpoints from '../../../../helpers/endpoints';
import syncRowHeights from '../../../../helpers/syncRowHeights';
import { GenericTable } from '../GenericTable';
import { PostcodeSearchModal } from '../modals/PostcodeSearchModal';
import { DuplicateWarningModal } from '../modals/DuplicateWarningModal';

export const ValuationOfficeAndCouncilTax = ({ translations, summaryData: data, selectedMatches, setSelectedMatches, prefix, disabledByUserType, setUserInput, setCurrentBrReference }) => {
  const [loading, setLoading] = useState(false);
  const [addressData, setAddressData] = useState({});
  const [filteringModalIsOpen, setFilteringModalIsOpen] = useState(false);
  const [postcodeSearch, setPostcodeSearch] = useState('');
  const [nameSearch, setNameSearch] = useState('');
  const [index, setIndex] = useState(0);
  const [source, setSource] = useState();
  const [duplicateWarningModal, setDuplicateWarningModal] = useState(false)
  const [duplicateWarningAccepted, setDuplicateWarningAccepted] = useState(false)

  const cycleIndex = useCallback((change = 0) => {
    if (loading || change === 0 ) return
    const totalIndex = (addressData.per_page * (addressData.current_page - 1)) + index
    if (totalIndex + change <= -1) return
    if (totalIndex + change >= ( addressData.total )) return
    if (totalIndex + change > addressData.to - 1 || totalIndex + change < addressData.from - 1) {
      setLoading(true);
      getData(null, change > 0 ? addressData.current_page + 1 : addressData.current_page - 1)
    }
    setIndex(index + change)
  }, [index, addressData])

  useEffect(() => {
    if (setCurrentBrReference) {
      setCurrentBrReference(addressData.data?.[index]?.BA_Reference_Number ?? "")
    }
  }, [index])

  // overrides lets callers bypass search state that hasn't committed yet (e.g. just reset on SPID change)
  const getData = (customInput, page, overrides = {}) => {
    const currentPostcodeSearch = overrides.postcodeSearch ?? postcodeSearch
    const currentNameSearch = overrides.nameSearch ?? nameSearch
    if (!customInput && !currentPostcodeSearch && (!data.Matched_UPRN_Postcode || data.Address_Status === 'Invalid Postcode') && !data.CMOS_Postcode) {
        setLoading(false);
        return;
    }
    setIndex(0)
    const oldData = addressData ?
        {...addressData, current_page: page ? addressData.current_page : 0}
      : 
        {}
    request(true).get(endpoints[`${prefix}_GET_VO_CT_COMBINED`], {
      doesCancel: true,
      params: {
        ba_reference_number: data.CF_VOA || data.Matched_VOA || data.CMOS_VOA,
        vo_postcode: customInput || currentPostcodeSearch || data.VO_Postcode || data.CMOS_Postcode || data.CMOS_Meter_Address_Postcode,
        ct_postcode: customInput || currentPostcodeSearch || data.VO_Postcode || data.CMOS_Postcode || data.CMOS_Meter_Address_Postcode,
        wholesaler: data.Wholesaler,
        spid: data.Core_SPID,
        page: page,
        name_search: currentNameSearch
      }
    }).then(e => {
      const voaCtData = e.data?.data ?? []
      voaCtData?.map(datum => {
        if (datum.Source !== "CT") return
        const addressLines = datum.address.split(", ");
        addressLines.forEach((line, i) => {
          datum['CT_Address_Line_' + (i + 1)] = line
          datum.council_tax_band = datum.council_tax_band === "Deleted" ? "" : datum.council_tax_band
          datum.ct_status = datum.council_tax_band === "" ? "Deleted" : "Active"
        })
        return datum
      })
      setAddressData(e.data);
      var displayFirst = -1;
      if ((data.CF_VOA || data.Matched_VOA || data.CMOS_VOA) && !oldData.current_page) {
        displayFirst = voaCtData.findIndex(_ => _.BA_Reference_Number === ( (data.CF_VOA && _.Postcode === data.CF_Postcode) || (data.Matched_VOA) || (data.CMOS_VOA)  ))
        setSelectedMatches((prevMatches) => {
          return {
            ...prevMatches,
            voa: displayFirst !== -1 ? voaCtData[displayFirst] : ''
          };
        })
      } else if (data.au_reference_number) {
        displayFirst = voaCtData.findIndex(_ => _.au_reference_number === data.au_reference_number)
        setSelectedMatches((prevMatches) => {
          return {
            ...prevMatches,
            ct: displayFirst !== -1 ? voaCtData[displayFirst] : ''
          };
        })
      }
      const startIndex = oldData.current_page > e.data.current_page ? e.data.per_page - 1 : 0
      const newIndex = displayFirst !== -1 ? displayFirst : startIndex 
      setIndex(newIndex);
      const currentRecord = voaCtData[newIndex] 
      if (setCurrentBrReference) {
        setCurrentBrReference(currentRecord?.BA_Reference_Number ?? "")
      }
      setSource(currentRecord?.BA_Reference_Number ? "voa" : currentRecord?.au_reference_number ? "ct" : '')
      setLoading(false)
    }).catch((error) => {
      console.log(error)
      window.alert('Could not get Valuation Office data');
      setAddressData({})
      setLoading(false)
    })
  }

  useEffect(() => {
    if (syncRowHeights) {
      syncRowHeights()
    }
    if (addressData.data?.[index] && source !== addressData.data[index].Source.toLowerCase()) {
      setSource(addressData.data[index].Source.toLowerCase())
    }
    setDuplicateWarningAccepted(false)
  }, [addressData, index, source])

  useEffect(() => {
    if (duplicateWarningAccepted === true) {
      selectMatch(source)
    }
  }, [duplicateWarningAccepted])

  useEffect(() => {
    setAddressData({})
    setIndex(-1)
    if (data.Core_SPID) {
      setIndex(-1)
      setPostcodeSearch('')
      setNameSearch('')
      setLoading(true)
      getData(data.VO_Postcode || data.CMOS_Postcode, undefined, { postcodeSearch: '', nameSearch: '' })
    } else {
      setLoading(false)
    }
  }, [data.Core_SPID])

  useEffect(() => {
    syncRowHeights()
  }, [translations])

  const selectMatch = useCallback((source) => {
    if (source === "voa" && !addressData.data?.[index]?.BA_Reference_Number) return;
    if (source === "ct" && !addressData.data?.[index]?.au_reference_number) return;
    if (addressData.data?.[index]?.Duplication_Status && duplicateWarningAccepted !== true) {
      setDuplicateWarningModal(true)
      return
    }
    setSelectedMatches((prevMatches) => ({
       ...prevMatches,
        voa: source === "voa" 
          ? addressData.data?.[index] 
          : '',
        ct: source === "ct" 
          ? addressData.data?.[index] 
          : ''
    }));
  }, [addressData, selectedMatches, setSelectedMatches, index, duplicateWarningAccepted]);

  const updateVoa = () => {
    setUserInput(prevUserInput => ({
      ...prevUserInput,
      CF_VOA: addressData.data?.[index]?.BA_Reference_Number,
      CF_VOA_Reference_Reason_Code: ''
    }))
  }

  const restorePostcode = useCallback(() => {
    if (setCurrentBrReference) {
      setCurrentBrReference("")
    }
    setIndex(-1)
    setPostcodeSearch(null); 
    setNameSearch(null);
    setAddressData({});
  }, [postcodeSearch, nameSearch])

  useEffect(() => {
    if (postcodeSearch === null && nameSearch === null) {
      setPostcodeSearch(''); 
      setNameSearch('');
      getData();
    }
  }, [postcodeSearch, nameSearch])

  const additionalHeaderInfo = useMemo(() => [source === "voa" ? "VOA" : source === "ct" ? "Council Tax" : ''], [source]);

  const rows = useMemo(() => {
    const matchEnabled = source === "voa" && JSON.stringify(selectedMatches.voa) === JSON.stringify(addressData.data?.[index]) ? true : source === "ct" && JSON.stringify(selectedMatches?.ct) === JSON.stringify(addressData.data?.[index]) ? true : false 

    return [
      [translations['BA_Reference_Number'], 'BA_Reference_Number'],
      [translations['VOA_Description'], ["SCOTTISH-W", "SW"].includes(data.Wholesaler) ? 'BR_Full_Address' : 'Primary_Description_Text'],
      [translations['Effective_Status'], ["SCOTTISH-W", "SW"].includes(data.Wholesaler) ? 'OCC_STATUS' : 'Effective_Status'],
      [translations['CT_Reference'], 'au_reference_number'],
      [translations['Effective_Status'], 'ct_status'],
      [translations['CT_Band'], 'council_tax_band'],
      [translations['CT_Mixed_Use_Property'], 'mixed_use_property'],
      [translations['Number_Or_Name'], source === "voa" ? ["SCOTTISH-W", "SW"].includes(data.Wholesaler) ? 'PAON' : 'Number_Or_Name' : ''],
      [translations['Street'], source === "voa" ? ["SCOTTISH-W", "SW"].includes(data.Wholesaler) ? 'STREET' : 'Street' : 'CT_Address_Line_1'],
      [translations['Sub_Street_level_1'], source === "voa" ? 'Sub_Street_Level_1' : 'CT_Address_Line_2'],
      [translations['Town'], source === "voa" ? ["SCOTTISH-W", "SW"].includes(data.Wholesaler) ? 'TOWN' : 'Town' : 'CT_Address_Line_3'],
      [translations['Postal_District'], source === "voa" ? ["SCOTTISH-W", "SW"].includes(data.Wholesaler) ? "ADMIN_AREA" : 'Postal_District' : 'CT_Address_Line_4'],
      [translations['County'], source === "voa" ? ["SCOTTISH-W", "SW"].includes(data.Wholesaler) ? "UNITARY_AUTHORITY" : 'County' : 'CT_Address_Line_5'],
      [translations['Postcode'], source === "voa" ? ["SCOTTISH-W", "SW"].includes(data.Wholesaler) ? "BR_Postcode" : 'Postcode' : 'postcode'],
      [source === "voa" ? 'Full_Property_Identifier' : 'address'],
      [
        () => <button disabled={disabledByUserType || source !== "voa"} className={`button compact smaller-text ${!disabledByUserType && addressData.data?.[index] && source === "voa" ? 'background-primary colour-white' : ''}`} onClick={() => updateVoa(source)}>Update VOA Reference</button>,
        () => <button disabled={disabledByUserType} className={`button compact smaller-text ${matchEnabled && !disabledByUserType ? 'background-primary colour-white' : ''}`} onClick={() => selectMatch(source)}>Select Match</button>
      ]
    ]
  }, [translations, window.reloadSummaryData, index, selectedMatches, addressData, syncRowHeights, source])

  return [
    <GenericTable title={translations['VOA_&_Council_Tax_Header']} rows={rows} data={addressData.data} paginationData={addressData} loading={loading} restorePostcode={restorePostcode} index={index} cycleIndex={cycleIndex} customPostcode={postcodeSearch || nameSearch} searchEnabled={true} setPostcodeModal={setFilteringModalIsOpen} additionalHeaderInfo={additionalHeaderInfo} />,
    <PostcodeSearchModal postcodeModal={filteringModalIsOpen} setPostcodeModal={setFilteringModalIsOpen} customPostcode={postcodeSearch} setCustomPostcode={setPostcodeSearch} nameSearch={nameSearch} setNameSearch={setNameSearch} setLoading={setLoading} getData={getData} />,
    <DuplicateWarningModal isOpen={duplicateWarningModal} setIsOpen={setDuplicateWarningModal} setAccepted={setDuplicateWarningAccepted} address={addressData.data?.[index]} />
  ]
}